// Prescription Service - Refactored to use backend API endpoints
// Removes direct Firestore access for security compliance

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
export class PrescriptionService {
  static async getPrescriptionsByFacility(facilityId, status = 'active') {
    try {
      const token = await getAuthToken();
      const queryParams = new URLSearchParams();
      queryParams.append('facilityId', facilityId);
      queryParams.append('status', status);
      
      const response = await fetch(`${API_BASE_URL}/clinical/prescriptions?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.prescriptions || [];
    } catch (error) {
      console.error('Error fetching prescriptions by facility:', error);
      throw error;
    }
  }

  static async getAllPrescriptions(status = 'active') {
    try {
      const token = await getAuthToken();
      const queryParams = new URLSearchParams();
      queryParams.append('status', status);
      
      const response = await fetch(`${API_BASE_URL}/clinical/prescriptions?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.prescriptions || [];
    } catch (error) {
      console.error('Error fetching all prescriptions:', error);
      throw error;
    }
  }

  static async getPatientData(patientId) {
    try {
      const { getPatient } = await import('./patientService');
      return await getPatient(patientId);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      throw error;
    }
  }

  static async getPatientsData(patientIds) {
    try {
      const { getPatient } = await import('./patientService');
      const patientsData = {};
      await Promise.all(
        patientIds.map(async (patientId) => {
          try {
            const patientData = await getPatient(patientId);
            if (patientData) {
              patientsData[patientId] = patientData;
            }
          } catch (error) {
            console.warn(`Error fetching data for patient ${patientId}:`, error);
          }
        })
      );
      return patientsData;
    } catch (error) {
      console.error('Error fetching patients data:', error);
      throw error;
    }
  }

  static async getFacilityData(facilityId) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/facility/facilities/${facilityId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching facility data:', error);
      throw error;
    }
  }

  static async getFacilitiesData(facilityIds) {
    try {
      const facilitiesData = {};
      for (const facilityId of facilityIds) {
        try {
          const facility = await this.getFacilityData(facilityId);
          if (facility) {
            facilitiesData[facilityId] = facility;
          }
        } catch (err) {
          console.warn(`Could not fetch facility ${facilityId}:`, err);
        }
      }
      return facilitiesData;
    } catch (error) {
      console.error('Error fetching facilities data:', error);
      throw error;
    }
  }
}

// Named exports for commonly used methods
export const createPrescription = async (prescriptionData) => {
  try {
    const token = await getAuthToken();
    // Use the clinical API for regular prescriptions
    const response = await fetch(`${API_BASE_URL}/clinical/prescriptions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(prescriptionData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating prescription:', error);
    throw error;
  }
};

export const getPatientPrescriptions = async (patientId, options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    if (options.status) {
      queryParams.append('status', options.status);
    }
    if (options.limit) {
      queryParams.append('limit', options.limit.toString());
    }
    if (options.encounterId) {
      queryParams.append('encounterId', options.encounterId);
    }

    const response = await fetch(`${API_BASE_URL}/clinical/patients/${patientId}/prescriptions?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.prescriptions || [];
  } catch (error) {
    console.error('Error fetching patient prescriptions:', error);
    throw error;
  }
};

export const updatePatientPharmacy = async (patientId, pharmacyData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/clinical/patients/${patientId}/pharmacy`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(pharmacyData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating patient pharmacy:', error);
    throw error;
  }
};

// Additional methods for comprehensive prescription management
export const getPrescriptionById = async (prescriptionId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/clinical/prescriptions/${prescriptionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching prescription by ID:', error);
    throw error;
  }
};

export const updatePrescription = async (prescriptionId, prescriptionData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/clinical/prescriptions/${prescriptionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(prescriptionData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating prescription:', error);
    throw error;
  }
};

export const deletePrescription = async (prescriptionId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/clinical/prescriptions/${prescriptionId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting prescription:', error);
    throw error;
  }
};

export const searchPrescriptions = async (searchTerm, options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    queryParams.append('search', searchTerm);
    
    if (options.facilityId) {
      queryParams.append('facilityId', options.facilityId);
    }
    if (options.status) {
      queryParams.append('status', options.status);
    }
    if (options.limit) {
      queryParams.append('limit', options.limit.toString());
    }

    const response = await fetch(`${API_BASE_URL}/clinical/prescriptions/search?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.prescriptions || [];
  } catch (error) {
    console.error('Error searching prescriptions:', error);
    throw error;
  }
};

export const getPrescriptionStatuses = async () => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/clinical/prescriptions/statuses`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching prescription statuses:', error);
    throw error;
  }
};
